import { useState, useSyncExternalStore } from 'react';
import { clearDeferredPrompt, getDeferredPrompt, subscribeInstall } from './install-prompt-store';

/**
 * Install affordances (ADR-0018). Two paths, because iOS Safari has neither `beforeinstallprompt` nor
 * a programmatic install:
 *   - CHROMIUM: read the deferred `beforeinstallprompt` event captured at app startup (see
 *     `install-prompt-store` — it must be caught early, since Chromium fires it once on load, before
 *     this component mounts) and expose `promptInstall()` for a custom "Install" button.
 *   - iOS SAFARI: detect a standalone-capable Safari that is NOT already installed, and expose an
 *     "Install" affordance the user can TAP to open the instructional "Add to Home Screen" guide
 *     (never auto-opened — a child's first session is not interrupted). An explicit "Not now"
 *     dismissal is remembered in localStorage so we don't keep offering.
 */

const IOS_DISMISS_KEY = 'kidshome.iosInstallDismissed';

/** Already running as an installed PWA (display-mode standalone, or iOS `navigator.standalone`). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return mq || iosStandalone;
}

/** True for iOS Safari (the only iOS engine that can add to Home Screen), excluding in-app webviews. */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports as Mac but has touch; treat a touch-capable "Mac" as iPad.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isIos) return false;
  // Exclude Chrome/Firefox/Edge on iOS (CriOS/FxiOS/EdgiOS) — they can't add to Home Screen.
  return /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

export interface InstallState {
  /** Chromium deferred-prompt available → render a custom Install button. */
  readonly canPromptInstall: boolean;
  /** Trigger the native Chromium install prompt (no-op if unavailable). */
  readonly promptInstall: () => Promise<void>;
  /** iOS Safari, not installed, not dismissed → offer the "Add to Home Screen" guide affordance. */
  readonly showIosInstall: boolean;
  /** The user said "Not now" — remember it so the affordance stops being offered. */
  readonly dismissIosGuide: () => void;
}

export function useInstallPrompt(): InstallState {
  // The deferred prompt is captured at app startup and lives in the module store, so it survives the
  // gap between Chromium firing `beforeinstallprompt` (early, on `/login`) and this hook mounting
  // (inside the family shell). `useSyncExternalStore` re-renders when it's captured or cleared.
  const deferred = useSyncExternalStore(subscribeInstall, getDeferredPrompt, () => null);
  const [iosDismissed, setIosDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(IOS_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const promptInstall = async (): Promise<void> => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    clearDeferredPrompt(); // a deferred prompt is single-use
  };

  const dismissIosGuide = (): void => {
    setIosDismissed(true);
    try {
      localStorage.setItem(IOS_DISMISS_KEY, '1');
    } catch {
      // private mode — overlay stays dismissed for the session via state.
    }
  };

  const showIosInstall = isIosSafari() && !isStandalone() && !iosDismissed;

  return { canPromptInstall: deferred !== null, promptInstall, showIosInstall, dismissIosGuide };
}
